import React, { useCallback, useRef, useState } from "react";
import { Button, Tooltip, Typography, message, theme } from "antd";
import { CloseOutlined, ExpandAltOutlined, MinusOutlined, PauseCircleFilled, PlayCircleFilled, SoundOutlined } from "@ant-design/icons";
import { useAudioPlayer, useAudioPlayerTrack } from "./AudioPlayerContext";
import type { AudioPlayerTrack } from "./AudioPlayerContext";
import "./AudioPlayerWidget.less";

const formatClock = (seconds: number) => {
  const s = Number.isFinite(seconds) && seconds > 0 ? Math.floor(seconds) : 0;
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
};

interface PlayerCardProps {
  track: AudioPlayerTrack;
  minimized: boolean;
  onToggleMinimized: () => void;
  onClose: () => void;
}

/**
 * Keyed by track.token in the widget, so playback state (time, playing) resets
 * for every recording without any effect-based syncing.
 *
 * The media element is always mounted — minimizing only hides it with CSS.
 * Unmounting it would stop the recording, and display:none does not.
 */
const PlayerCard: React.FC<PlayerCardProps> = ({ track, minimized, onToggleMinimized, onClose }) => {
  const { token } = theme.useToken();
  const mediaRef = useRef<HTMLMediaElement | null>(null);
  const setMediaEl = useCallback((el: HTMLMediaElement | null) => {
    mediaRef.current = el;
  }, []);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState({ current: 0, duration: 0 });

  const syncTime = () => {
    const el = mediaRef.current;
    if (el) setTime({ current: el.currentTime, duration: el.duration });
  };

  const togglePlay = () => {
    const el = mediaRef.current;
    if (!el) return;
    if (el.paused) {
      el.play().catch(() => message.error("This recording could not be played"));
    } else {
      el.pause();
    }
  };

  // MediaRecorder-style uploads (webm) often report an Infinity/NaN duration.
  const hasDuration = Number.isFinite(time.duration) && time.duration > 0;
  const percent = hasDuration ? Math.min(100, (time.current / time.duration) * 100) : 0;
  const timeLabel = hasDuration ? `${formatClock(time.current)} / ${formatClock(time.duration)}` : formatClock(time.current);

  const mediaProps = {
    src: track.url,
    controls: true,
    autoPlay: true,
    className: "apw__media",
    onPlay: () => setPlaying(true),
    onPause: () => setPlaying(false),
    onEnded: () => setPlaying(false),
    onTimeUpdate: syncTime,
    onLoadedMetadata: syncTime,
    onDurationChange: syncTime,
    onError: () => message.error("This recording could not be played"),
  };

  const classes = ["apw", minimized ? "apw--minimized" : "", track.kind === "video" ? "apw--video" : ""].filter(Boolean).join(" ");

  return (
    <div className={classes} role="region" aria-label="Recording player">
      <div className="apw__header">
        {minimized ? (
          <Button
            type="text"
            className="apw__toggle"
            aria-label={playing ? "Pause" : "Play"}
            icon={playing ? <PauseCircleFilled /> : <PlayCircleFilled />}
            onClick={togglePlay}
          />
        ) : (
          <SoundOutlined className="apw__icon" />
        )}
        <div className="apw__meta">
          <Typography.Text strong ellipsis title={track.title}>
            {track.title}
          </Typography.Text>
          <Typography.Text type="secondary" ellipsis className="apw__subtitle">
            {minimized ? timeLabel : track.subtitle}
          </Typography.Text>
        </div>
        <div className="apw__actions">
          <Tooltip title={minimized ? "Expand" : "Minimize"}>
            <Button
              type="text"
              size="small"
              aria-label={minimized ? "Expand player" : "Minimize player"}
              icon={minimized ? <ExpandAltOutlined /> : <MinusOutlined />}
              onClick={onToggleMinimized}
            />
          </Tooltip>
          <Tooltip title="Close">
            <Button type="text" size="small" aria-label="Close player" icon={<CloseOutlined />} onClick={onClose} />
          </Tooltip>
        </div>
      </div>

      <div className="apw__body">
        {track.kind === "video" ? <video ref={setMediaEl} {...mediaProps} /> : <audio ref={setMediaEl} {...mediaProps} />}
      </div>

      {minimized && hasDuration && (
        <div className="apw__progress">
          <div className="apw__progress-bar" style={{ width: `${percent}%`, background: token.colorPrimary }} />
        </div>
      )}
    </div>
  );
};

/**
 * Site-wide floating recording player, docked bottom-left. Expanded it shows the
 * native media controls (seek, volume, speed, download); minimized it collapses
 * to a compact play/pause + progress pill so users can keep working while it
 * plays. Starts from anywhere via useAudioPlayer().play(); mount once inside
 * <AudioPlayerProvider> (app.tsx childrenRender).
 */
const AudioPlayerWidget: React.FC = () => {
  const track = useAudioPlayerTrack();
  const { close } = useAudioPlayer();
  // Lives here rather than in PlayerCard so the choice carries over when the
  // next recording is started.
  const [minimized, setMinimized] = useState(false);

  if (!track) return null;

  return <PlayerCard key={track.token} track={track} minimized={minimized} onToggleMinimized={() => setMinimized((m) => !m)} onClose={close} />;
};

export default AudioPlayerWidget;
